import { CLIP_LIFECYCLE_DOC_URL, CLIP_LIFECYCLE_DOC_TITLE } from '../../utils/docLinks';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router';
import { Download, Filter as FilterIcon, X } from 'lucide-react';
import axiosInstance from '../../utils/axiosInstance';
import config from '../../utils/config';
import { Device, DeviceSearchRow } from '../../types';
import { useDeviceSearch, DeviceSearchParams, TagFilterChanges } from '../../hooks/useDeviceSearch';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useFirestoreEnrichment } from '../../hooks/useFirestoreEnrichment';
import { useEntityPathOptions } from '../../hooks/useEntityPathOptions';
import { useAuth } from '../../context/AuthContext';
import { ConfirmOptions, useConfirm } from '../../context/ConfirmContext';
import { validateEntityPath } from '../EntityPathField';
import { deviceDraftChanged, deviceUpdateBody, entityPathChanged } from '../../utils/deviceEdit';
import DeviceAppUserData from '../DeviceAppUserData';
import DeviceRecordFields from '../DeviceRecordFields';
import {
  COLUMN_BY_KEY,
  COLUMN_REGISTRY,
  ColumnDef,
  DEFAULT_MIN_WIDTH,
  FILTERABLE_BY_FILTER_KEY,
  filterKeyOf,
  sortKeyOf,
} from './columnRegistry';
import {
  MAX_SORT_LEVELS,
  SortLevel,
  filtersFromSearchParams,
  isExplorerUrlBare,
  normalizeLegacyParams,
  parseSortParam,
  serializeSort,
} from './searchParamCodec';
import {
  DeviceListPrefs,
  DeviceListView,
  PAGE_SIZES,
  decodeView,
  defaultPrefs,
  readPrefs,
  writePrefs,
} from './deviceListPrefs';
import ColumnChooser from './ColumnChooser';
import { ActiveFilterRow, FILTER_DEBOUNCE_MS, FilterPicker } from './FilterBar';
import { healTagFilterValue } from './tagFilterHealing';
import { deviceListCsv, deviceListCsvFilename, fetchAllDeviceRows } from './deviceListCsv';
import { downloadCsv } from '../../utils/chartCsv';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { own } from '../../utils/ownKey';
import DeviceTagsPanel from '../DeviceTagsPanel';
import Button from '../ui/Button';
import ConfirmDialog from '../ConfirmDialog';
import { Input } from '../pilot-ui/input';
import { Label } from '../pilot-ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../pilot-ui/select';
import { Checkbox } from '../pilot-ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../pilot-ui/tooltip';
import {
  OPEN_IN_DEVICES_CAP,
  devicesHrefForIds,
  fetchAllMatchingIds,
  useDeviceSelection,
} from './deviceListSelection';
import SelectionBar from './SelectionBar';
import {
  Report,
  ReportSummary,
  UpdateReportBody,
  createReport,
  deleteReport,
  fetchReport,
  isAbortError,
  listReports,
  reportErrorCode,
  reportErrorStatus,
  updateReport,
} from '../../api/deviceListReports';
import {
  REPORT_PARAM,
  REPORT_VIEW_PARAM,
  defaultDeviceListView,
  defaultViewUrlParams,
  hasReportViewMarker,
  reportIdFromParams,
  reportUrlParams,
  sameReportState,
  toReportState,
  urlCarriesData,
  viewsDiffer,
  withoutReportParams,
  workingViewUrlParams,
} from './reportView';
import ReportMenu, { REPORT_MENU_TRIGGER_ID } from './ReportMenu';
import {
  ReportNameDialog,
  SaveSharedReportDialog,
  ShareReportDialog,
  StaleReportDialog,
  WriteOutcome,
} from './ReportDialogs';
import { Lease, useOpeningLifecycle } from './useOpeningLifecycle';

const REPORT_TRIGGER_SELECTOR = `#${REPORT_MENU_TRIGGER_ID}`;

/**
 * Plan 101 — Device List (`/edit-device`): the operator device table with
 * full data-exploration controls, keeping this page's editing role.
 *
 * The table is backed by GET /devices/search (server-side scope filtering,
 * typed `f.<key>` filters, multi-level sort, facet option lists, free-text,
 * page-number pagination) and rendered from the column registry. The page's
 * original capabilities are integrated, not replaced:
 *  - the Edit modal (write_devices-gated) — Actions column, device fetched
 *    fresh by id on open, table refetched after save;
 *  - the Source column (App vs Manual) from the live Firestore enrichment —
 *    a registry column (hideable/reorderable) whose cell content is supplied
 *    by this page (Plan 103 follow-up);
 *  - the app-data read-only panel inside the modal;
 *  - row click opens the device workspace (/devices/:id); interactive
 *    elements inside the row are exempt;
 *  - FS-882: row checkboxes and a select-all header feed a selection bar
 *    whose Open in Devices hands the selected ids to the Devices page
 *    (deviceListSelection.ts);
 *  - FS-889 (Plan 132): the view can be saved as a named report, opened
 *    again, shared at a scope path and copied (reportView.ts, ReportMenu,
 *    ReportDialogs; the API in api/deviceListReports.ts).
 *
 * State model:
 *  - URL search params are the session source of truth for the DATA VIEW
 *    (q, `f.<key>` filters, `sort=key:dir,...`, page, page_size) so any view
 *    is shareable/bookmarkable. localStorage (deviceListPrefs.v1.<userId>)
 *    re-injects the saved data view ONLY when the URL arrives bare.
 *  - LAYOUT (column order, hidden set, widths) is client-only, never in the
 *    URL, and always restored from localStorage — a shared link reproduces
 *    the sender's data view but never their column layout.
 *  - REPORTS: `report=<id>` names the open report; `rv=1` says the URL holds
 *    the complete data view (missing params mean empty), so a reload, Back
 *    or Forward never re-applies the report's saved data — a cleared last
 *    filter stays cleared. `report` without `rv` is a link: the report is
 *    applied once (or, when the link carries its own data, only its layout)
 *    and `rv=1` is written. Choosing a report from the menu always applies
 *    it. Every application is an opening, and every asynchronous job (a
 *    report read or write, the list, the search, a CSV walk, a confirmation
 *    still on screen) holds a lease on the account and opening it started
 *    under: one that answers after the page moved on is dropped
 *    (useOpeningLifecycle.ts). FS-900: a write the page moved past that
 *    answers "you no longer see this report", or any report answer sent
 *    under a token the account no longer holds, puts the open report in
 *    question; a fresh read decides, without applying anything over the view
 *    on screen (`checkVisibility`), and the report's metadata only ever moves
 *    to a newer copy (`noteCopy`).
 */

type SortDir = 'asc' | 'desc';

const DEFAULT_PAGE_SIZE = 20;

/** Page-level trailing column (Actions): fixed at the right edge, not
 * sortable/filterable/hideable — it's page behavior, not data. Source is a
 * registry column (hideable/reorderable); its CELL content is still supplied
 * here because it reads the live Firestore enrichment map. */
const TRAILING_WIDTHS = { actions: 90 };

/** FS-882: the leading checkbox column — page behavior like Actions, not a
 * registry column, so it carries no data-colkey (CSV, layout prefs and the
 * e2e header helpers never see it). */
const SELECT_COL_WIDTH = 40;

/** Display label for a sort key: the first registry column that drives it. */
const sortLabel = (key: string): string =>
  COLUMN_REGISTRY.find((c) => c.sortable && sortKeyOf(c) === key)?.label ?? key;

/**
 * FS-889 — how an open report is resolved against the URL it arrived in:
 *  - `apply`: a report link with no data of its own — apply data + layout,
 *    then write `rv=1`;
 *  - `layout`: a report link that carries its own filters / search / sort —
 *    keep them, take the report's layout, write `rv=1`;
 *  - `baseline`: the URL already holds the working view and the per-user
 *    prefs hold this report's working layout — fetch only for the saved
 *    baseline and `can_*` flags, never apply;
 *  - `baseline+layout`: as `baseline`, but the layout is the report's (Back
 *    or Forward to a report whose layout the prefs do not hold).
 * Every mode but `baseline` is needed for the first render: the search, the
 * prefs write-through, CSV and select-all wait until it lands.
 */
type OpenMode = 'apply' | 'layout' | 'baseline' | 'baseline+layout';

interface OpenReport {
  id: number;
  mode: OpenMode;
  /** The URL write this opening intends: a push for a report chosen from the
   *  menu, a replace for a link. Retry keeps it, so a menu choice that fails
   *  and then succeeds on Retry still leaves the previous report one step
   *  Back. */
  push: boolean;
  status: 'loading' | 'ready' | 'error';
  /** The last saved copy: the rights, the concurrency token and the state as
   *  the server holds it, as far as this page knows. */
  report: Report | null;
  /** What Modified compares against and Revert puts back. Normally the
   *  report's decoded state; it stays behind when a save that Revert retired
   *  turns out to have committed (`patchReport`). */
  baseline: DeviceListView | null;
  /** What `decodeView` had to drop from the saved state (an `adjusted` load). */
  adjustments: string[];
  error: string | null;
}

const needsFirstRender = (mode: OpenMode) => mode !== 'baseline';

/**
 * FS-900 — a write the page had moved past (Revert, Back or Forward, the
 * report chosen again) answered that the caller may no longer see the open
 * report (an Unshare's `{report: null}`, or a 404), or a write was answered
 * under a token the account no longer holds. Until a fresh read under the
 * current opening and token answers, the report's visibility is in question
 * (`checkVisibility`), and that read only checks: it never applies the saved
 * view. `refusal`: the server refused to read the report (403), with its
 * message; the report's own actions stay off until a read succeeds.
 */
interface VisibilityCheck {
  status: 'reading' | 'error';
  error: string | null;
  refusal: string | null;
}

/**
 * FS-900 — what the page knows of the open report across its openings. Kept
 * while the same report stays open (Back, Forward, Revert, Retry, choosing it
 * again); started afresh when another report opens; dropped when the report
 * closes or the page unmounts (the page is keyed by account, so an account
 * change drops it too). Closing the menu or a dialog leaves it alone.
 */
interface ReportRecord {
  id: number;
  /** The newest copy of the row any read or write answered with (`noteCopy`). */
  newest: Report | null;
  visibility: VisibilityCheck | null;
}

interface InitialPlan {
  /**
   * The URL the page writes (replace) on mount before anything else runs:
   * the per-user working view restored into a bare URL, a legacy URL
   * normalized, or a bare URL after an account switch. Null when the first
   * URL stands as it is.
   */
  initialUrl: URLSearchParams | null;
  open: { id: number; mode: OpenMode } | null;
}

/**
 * Decide, once from the first URL and the per-user prefs, what the page
 * shows. `accountSwitched`: the account changed while the page was mounted,
 * so the URL on screen is the previous account's working view; this account
 * starts from its own saved view, as if it had arrived on a bare URL. A first
 * mount with a link keeps the link's normal precedence.
 */
function planInitialView(first: URLSearchParams, prefs: DeviceListPrefs | null, accountSwitched: boolean): InitialPlan {
  const sp = accountSwitched ? defaultViewUrlParams(first) : first;
  const initialUrl = accountSwitched ? sp : normalizeLegacyParams(sp);
  const reportId = reportIdFromParams(sp);
  const rv = hasReportViewMarker(sp);
  if (reportId !== null) {
    if (!rv) return { initialUrl, open: { id: reportId, mode: urlCarriesData(sp) ? 'layout' : 'apply' } };
    return { initialUrl, open: { id: reportId, mode: prefs?.reportId === reportId ? 'baseline' : 'baseline+layout' } };
  }
  if (rv || !prefs || !isExplorerUrlBare(sp)) return { initialUrl, open: null };
  const hasView =
    Object.keys(prefs.filters).length > 0 ||
    prefs.sort !== '' ||
    prefs.pageSize !== DEFAULT_PAGE_SIZE ||
    prefs.q !== '' ||
    prefs.reportId !== null;
  if (!hasView) return { initialUrl, open: null };
  const next = workingViewUrlParams(sp, {
    ...defaultDeviceListView(),
    filters: prefs.filters,
    sort: prefs.sort,
    pageSize: prefs.pageSize,
    q: prefs.q,
  });
  if (prefs.reportId !== null) {
    next.set(REPORT_PARAM, String(prefs.reportId));
    next.set(REPORT_VIEW_PARAM, '1');
  }
  return { initialUrl: next, open: prefs.reportId !== null ? { id: prefs.reportId, mode: 'baseline' } : null };
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const isNotFound = (err: unknown) => reportErrorCode(err) === 'not_found' || reportErrorStatus(err) === 404;

/**
 * FS-900 — the token a request goes out under, read from storage as the
 * request layer reads it (utils/axiosInstance.ts). A report answered for a
 * token the account no longer holds (a newer one arrived for this account)
 * carries that token's rights and its word on what the caller can see, so it
 * is never taken as it stands; the report is read again instead. Read
 * without validating: AuthContext signs out a token near its expiry when
 * asked for it, and a freshness check must never sign anyone out.
 */
const storedToken = (): string | null => localStorage.getItem('token');

/**
 * FS-889 — the history state on every URL the page writes itself (a filter
 * edit, the search text, paging, a sort, a report installed or closed). The
 * location effect reads it to tell those from true navigation — Back,
 * Forward, a link — which is the only thing that changes the opening: an
 * edit to the view still on screen while another report loads must not be
 * read as going back to that view's report.
 */
const PAGE_URL_WRITE = { deviceListUrlWrite: true } as const;
const isPageUrlWrite = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && (state as { deviceListUrlWrite?: unknown }).deviceListUrlWrite === true;

/** Either dialog the report menu can open besides the useConfirm ones. */
type ReportDialog =
  | { kind: 'name'; purpose: 'create' | 'copy' | 'rename'; view: DeviceListView }
  | {
      kind: 'share';
      report: Report;
      candidate: DeviceListView;
      candidateIsWorkingView: boolean;
      /** The candidate is sent as the report's state with the share: it is
       *  the working view, or the saved one differs from what the server
       *  holds (an adjusted load, or a retired save that committed). */
      savesState: boolean;
    }
  | { kind: 'saveShared'; report: Report; view: DeviceListView }
  | { kind: 'stale'; report: Report }
  | null;

/**
 * FS-889: everything below is keyed by the signed-in account, so a token for
 * another account (AuthContext can swap identities without a remount) drops
 * every piece of state, aborts every request and re-reads that account's own
 * preferences and selection before anything is written. The page also notes
 * that the account changed under it: the URL then holds the previous
 * account's working view, which the new account must not inherit.
 */
const DeviceList: React.FC = () => {
  const { id: userId } = useAuth();
  const seen = useRef({ userId, switched: false });
  if (seen.current.userId !== userId) seen.current = { userId, switched: true };
  return <DeviceListForUser key={userId} userId={userId} accountSwitched={seen.current.switched} />;
};

const DeviceListForUser: React.FC<{ userId: number; accountSwitched: boolean }> = ({ userId, accountSwitched }) => {
  const { hasPermission, scopes } = useAuth();
  const canWrite = hasPermission('write_devices');
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  // Every URL write of the page's own goes through here, marked as such. It
  // starts from the URL as it is NOW, whichever render the caller came from:
  // a write answered late (a Delete, an Unshare) closes the report over the
  // edits made while it was in flight, not over the URL it was sent under.
  const latestUrl = useRef({ searchParams, setSearchParams });
  latestUrl.current = { searchParams, setSearchParams };
  const writeUrl = useCallback(
    (next: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams), { replace = false } = {}) => {
      const { searchParams: prev, setSearchParams: set } = latestUrl.current;
      set(typeof next === 'function' ? next(prev) : next, { replace, state: PAGE_URL_WRITE });
    },
    [],
  );
  const confirm = useConfirm();
  // Every asynchronous job on this page takes a lease here and asks it
  // before touching anything (useOpeningLifecycle.ts).
  const lifecycle = useOpeningLifecycle(userId);

  // Live Firestore enrichment: Source column + the modal's app-data panel.
  const { enrichmentMap, loading: enrichmentLoading, error: enrichmentError } = useFirestoreEnrichment();
  // Plan 83: only fetch dropdown options for users who can edit devices.
  const { options: entityPathOptions, error: entityPathOptionsError } = useEntityPathOptions(canWrite);

  // ---------------------------------------------------------------------
  // Preferences (read once) + layout state
  // ---------------------------------------------------------------------
  const initialPrefs = useMemo(() => readPrefs(userId), [userId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const plan = useMemo(() => planInitialView(searchParams, initialPrefs, accountSwitched), []);
  const [columnOrder, setColumnOrder] = useState<string[]>(
    () => initialPrefs?.columnOrder ?? defaultPrefs().columnOrder,
  );
  const [hidden, setHidden] = useState<Set<string>>(
    () => new Set(initialPrefs?.hidden ?? defaultPrefs().hidden),
  );
  const [widths, setWidths] = useState<Record<string, number>>(() => initialPrefs?.widths ?? {});

  // The URL the page is about to write, decided once from the FIRST URL and
  // the per-user prefs: a URL that already carries explorer params wins
  // untouched (shared links reproduce the shared data view exactly). Read AS
  // the URL until the write has landed, so the first search, the gate and
  // the prefs write already see it.
  //
  // CONSUMED ONCE — cleared by the injection effect below. Keeping it live
  // afterwards made it a second, competing source of truth: any later return
  // to a bare URL re-applied the saved view, so clearing the last filter
  // (which is exactly what happens when a tag in a saved view is deleted) put
  // the filter straight back and it could not be removed at all.
  const [pendingUrl, setPendingUrl] = useState(() => plan.initialUrl);
  const sp = pendingUrl ?? searchParams;

  // ---------------------------------------------------------------------
  // FS-889 — the open report, its opening and the gate
  // ---------------------------------------------------------------------
  const [open, setOpen] = useState<OpenReport | null>(() =>
    plan.open
      ? { id: plan.open.id, mode: plan.open.mode, push: false, status: 'loading', report: null, baseline: null, adjustments: [], error: null }
      : null,
  );
  const openRef = useRef(open);
  openRef.current = open;
  // FS-900: read and written as answers arrive, not from a render, so two
  // answers landing before the next render are compared with each other.
  const record = useRef<ReportRecord | null>(
    plan.open ? { id: plan.open.id, newest: null, visibility: null } : null,
  );
  // The record's visibility question, for rendering (the gate, the banner, the menu).
  const [visibility, setVisibilityShown] = useState<VisibilityCheck | null>(null);
  // Bumped by every application (a report applied, Revert, Default view, a
  // history landing) and used as the filter editors' key, so a debounced
  // editor from the previous view cannot write its old value into the new one.
  const [editorsGen, setEditorsGen] = useState(0);
  const [dialog, setDialog] = useState<ReportDialog>(null);
  // The report trigger: every dialog gives focus back to it.

  /** A new opening: nothing of the previous one lands, and its dialog goes with it. */
  const newOpening = () => {
    setDialog(null);
    return lifecycle.newOpening();
  };

  /**
   * FS-900: the record follows the open report — kept for the same one,
   * started afresh for another, dropped for none. Any visibility question
   * ends here: an opening's own read answers it.
   */
  const followReport = (id: number | null) => {
    if (id === null) record.current = null;
    else if (record.current?.id !== id) record.current = { id, newest: null, visibility: null };
    else record.current.visibility = null;
    setVisibilityShown(null);
  };

  const setVisibility = (id: number, next: VisibilityCheck | null) => {
    if (record.current?.id !== id) return;
    record.current.visibility = next;
    setVisibilityShown(next);
  };

  /**
   * FS-900: a copy of the open report from a read or a write answer. The
   * metadata (name, rights, owner, token) only moves forward. A read is kept
   * at the row version held or a newer one: at the same version it still
   * brings what the server worked out for this request (the rights, a
   * deleted owner). A write is kept only when newer: at the same version a
   * read has already seen its commit, and later. Returns the copy to show,
   * whether this one was kept and whether it is older than the newest held,
   * or null when `id` is not the open report.
   */
  const noteCopy = (
    id: number,
    copy: Report,
    from: 'read' | 'write',
  ): { held: Report; kept: boolean; older: boolean } | null => {
    const rec = record.current;
    if (rec?.id !== id) return null;
    const order = rec.newest === null ? 1 : Math.sign(Date.parse(copy.updated_at) - Date.parse(rec.newest.updated_at));
    if (order > 0 || (order === 0 && from === 'read')) {
      rec.newest = copy;
      return { held: copy, kept: true, older: false };
    }
    return { held: rec.newest ?? copy, kept: false, older: order < 0 };
  };

  // The gate: nothing searches, persists or walks the list while the view on
  // screen is not yet the one that will be. Decided in the same render the
  // URL changes: a history landing names a report the page has not resolved
  // (so does a report just chosen, until its URL is written), or a report
  // needed for the first render is still loading. An opening whose read gave
  // way to a visibility check is not: that read never applies anything, so
  // the view on screen is already the one that will be.
  const urlReportId = reportIdFromParams(sp);
  const gating =
    (open?.id ?? null) !== urlReportId ||
    (open !== null && open.status !== 'ready' && visibility === null && needsFirstRender(open.mode));
  const gatingRef = useRef(gating);
  gatingRef.current = gating;

  const [reports, setReports] = useState<ReportSummary[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [unsupportedIds, setUnsupportedIds] = useState<ReadonlySet<number>>(() => new Set());
  const [reportNotice, setReportNotice] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);

  // The list outlives openings; only the latest request's answer counts, so
  // a slow first listing cannot bring back a report a later one has dropped.
  const listLease = useRef<Lease | null>(null);
  const refreshList = useCallback(() => {
    listLease.current?.abort();
    const lease = lifecycle.lease('account');
    listLease.current = lease;
    listReports(lease.signal)
      .then((list) => {
        if (!lease.alive()) return;
        setReports(list);
        setListError(null);
      })
      .catch((err) => {
        if (!lease.alive() || isAbortError(err)) return;
        setListError(extractErrorMessage(err));
      })
      .finally(() => lease.release());
  }, [lifecycle]);
  useEffect(() => {
    refreshList();
  }, [refreshList]);

  // Write the planned URL (replace, so Back doesn't step through the
  // rewrite), and start resolving the report the first URL or the restored
  // view names. Under StrictMode this runs, is cleaned up and runs again: the
  // lifecycle's cleanup retires the first opening, and the second run starts
  // its own.
  useEffect(() => {
    if (plan.initialUrl) writeUrl(plan.initialUrl, { replace: true });
    // The planned URL is now the real one, the single source of truth from
    // here on.
    setPendingUrl(null);
    if (plan.open) startOpen(plan.open.id, plan.open.mode, { push: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------
  // Data view (URL-backed; the planned URL until it is written)
  // ---------------------------------------------------------------------
  const filters = filtersFromSearchParams(sp);
  const sortLevels: SortLevel[] = parseSortParam(sp.get('sort'), sp.get('dir'));
  const sortStr = serializeSort(sortLevels);
  const page = Math.max(parseInt(sp.get('page') ?? '1', 10) || 1, 1);
  const pageSize = parseInt(sp.get('page_size') ?? '', 10) || DEFAULT_PAGE_SIZE;
  const urlQ = sp.get('q') ?? '';

  const [qInput, setQInput] = useState(urlQ);
  const debouncedQ = useDebouncedValue(qInput, FILTER_DEBOUNCE_MS);

  // Push the debounced query into the URL (source of truth for the fetch) and
  // reset to page 1 — but ONLY when the query text actually changes. Without
  // the ref guard this effect re-runs on every render (setSearchParams isn't a
  // stable identity) and would wipe the `page` param back to 1.
  //
  // FS-889: only once the debounced value has caught up with the box. A
  // report applied while a keystroke was still settling sets the box and
  // `prevQ` together; the stale debounced value must not land in between.
  const prevQ = useRef(urlQ);
  const pushQ = useCallback(
    (q: string) => {
      prevQ.current = q;
      writeUrl(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (q) next.set('q', q);
          else next.delete('q');
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [writeUrl],
  );
  useEffect(() => {
    if (debouncedQ !== qInput) return;
    if (debouncedQ === prevQ.current) return;
    pushQ(debouncedQ);
  }, [debouncedQ, qInput, pushQ]);
  // Leaving the box commits at once (Save blurs it before capturing the view).
  const commitQNow = () => {
    if (qInput !== prevQ.current) pushQ(qInput);
  };

  const params: DeviceSearchParams = {
    q: urlQ || undefined,
    filters,
    sort: sortStr || undefined,
    page,
    pageSize,
  };

  /**
   * FS-501: a merge retires a tag id, and a saved view still holds the old one.
   * The server resolves it through the alias table for the request in flight;
   * this rewrites the stored filter so the healing is permanent (the
   * write-through effect below persists it), and tells the operator when a tag
   * in their saved view was deleted rather than merged — a `Not Tagged` filter
   * losing its last value stops hiding anything, which they should not have to
   * discover by noticing devices they thought were hidden.
   *
   * FS-889: it changes the working view only; the saved report changes when
   * someone with rights saves it. Nothing arrives while a report is loading
   * (the search is off), and the search hook drops an answer from before a
   * switch or an unmount before it gets here.
   */
  const [goneTagFilters, setGoneTagFilters] = useState<string[]>([]);
  const handleTagFilterChanges = (changes: TagFilterChanges) => {
    if (gatingRef.current) return;
    const goneNow: string[] = [];
    for (const [fk, change] of Object.entries(changes)) {
      const current = filters[fk];
      if (!current) continue;
      const next = healTagFilterValue(current, change);
      if (next !== current) setParam(`f.${fk}`, next || null);
      goneNow.push(...change.gone);
    }
    if (goneNow.length > 0) setGoneTagFilters(goneNow);
  };

  const { rows, rowsCurrent, facets, total, loading, error, pageCount, refetch } = useDeviceSearch(
    params,
    handleTagFilterChanges,
    { enabled: !gating },
  );

  // ---------------------------------------------------------------------
  // Write-through persistence (data view + layout + open report); held
  // while the gate is closed.
  // ---------------------------------------------------------------------
  const filtersKey = JSON.stringify(filters);
  const openId = open?.id ?? null;
  useEffect(() => {
    if (gating) return;
    writePrefs(userId, {
      columnOrder,
      hidden: [...hidden],
      widths,
      filters: JSON.parse(filtersKey),
      sort: sortStr,
      pageSize,
      q: urlQ,
      reportId: openId,
    });
  }, [userId, gating, columnOrder, hidden, widths, filtersKey, sortStr, pageSize, urlQ, openId]);

  // The view on screen, as the report would store it; read by Save after the
  // editors have settled.
  const currentView: DeviceListView = {
    columnOrder,
    hidden: [...hidden],
    widths,
    filters,
    sort: sortStr,
    pageSize,
    q: urlQ,
  };
  const viewRef = useRef(currentView);
  viewRef.current = currentView;

  const modified =
    open !== null && open.status === 'ready' && open.baseline !== null && !gating && viewsDiffer(currentView, open.baseline);
  // FS-924: Save would store exactly what the newest copy the page holds
  // already has. Unlike Modified this counts widths, which a report stores.
  const nothingToSave =
    open !== null &&
    open.status === 'ready' &&
    open.report !== null &&
    !gating &&
    sameReportState(toReportState(currentView), open.report.state);

  // A facet's label for a value, for the share confirmations (tag ids read
  // as their names).
  const facetLabel = (filterKey: string, value: string): string | undefined => {
    const col = own(FILTERABLE_BY_FILTER_KEY, filterKey);
    const options = col?.facet ? facets[col.facet] : undefined;
    return options?.find((o) => String(o.value) === value)?.label ?? col?.optionLabel?.(value);
  };

  // ---------------------------------------------------------------------
  // FS-889 — opening, applying and closing reports
  // ---------------------------------------------------------------------

  /**
   * The editors take what the URL says: the search box and its debounce,
   * no pending chips, fresh filter editors. A keystroke still settling in
   * the view being left dies with its editor instead of writing that view's
   * URL once more.
   */
  const resetEditorsToUrl = () => {
    setQInput(urlQ);
    prevQ.current = urlQ;
    setPendingFilters([]);
    setEditorsGen((g) => g + 1);
  };

  /** Put a view on screen: URL, layout, search box, no pending chips, fresh editors. */
  const applyView = (view: DeviceListView, reportId: number | null, push: boolean, keepData = false) => {
    setEditorsGen((g) => g + 1);
    setColumnOrder(view.columnOrder);
    setHidden(new Set(view.hidden));
    setWidths(view.widths);
    if (!keepData) {
      setQInput(view.q);
      prevQ.current = view.q;
    }
    setPendingFilters([]);
    writeUrl(
      (prev) => (reportId === null ? defaultViewUrlParams(prev) : reportUrlParams(prev, view, reportId, { keepData })),
      { replace: !push },
    );
  };

  /** Close the report; the view on screen stays as an unsaved view. */
  const closeReportKeepView = () => {
    newOpening();
    followReport(null);
    setOpen(null);
    writeUrl((prev) => withoutReportParams(prev), { replace: true });
  };

  /** The report is not there any more (a read or a write answered 404). */
  const reportGone = () => {
    closeReportKeepView();
    setReportNotice('That report is no longer available.');
    refreshList();
  };

  /** The caller no longer sees the report (an admin unshared someone else's). */
  const reportNoLongerSeen = () => {
    closeReportKeepView();
    setReportNotice('You no longer see this report; the view on screen is kept as an unsaved view.');
    refreshList();
  };

  /**
   * The GET of the open report in flight, if any. Only the latest one
   * counts: a fresh read of the same report retires the one before it,
   * whose answer may describe the row as it was before a write committed.
   */
  const openFetch = useRef<Lease | null>(null);

  /**
   * One GET of the open report under the current opening. The answer lands
   * through `onReport` only while the lease is alive; 404 goes to `onGone`,
   * any other failure to `onError`; an aborted read lands nowhere. One sent
   * under a token the account no longer holds is asked again under the
   * current one, with the same handlers.
   */
  const fetchOpenReport = (
    id: number,
    handle: { onReport: (report: Report) => void; onGone: () => void; onError: (err: unknown) => void },
  ) => {
    openFetch.current?.abort();
    const lease = lifecycle.lease('read');
    openFetch.current = lease;
    const askedUnder = storedToken();
    void (async () => {
      try {
        const report = await fetchReport(id, lease.signal);
        if (!lease.alive()) return;
        if (storedToken() !== askedUnder) fetchOpenReport(id, handle);
        else handle.onReport(report);
      } catch (err) {
        if (!lease.alive() || isAbortError(err)) return;
        if (storedToken() !== askedUnder) fetchOpenReport(id, handle);
        else if (isNotFound(err)) handle.onGone();
        else handle.onError(err);
      } finally {
        lease.release();
        if (openFetch.current === lease) openFetch.current = null;
      }
    })();
  };

  /** Saved by a newer console: never applied; the report closes and the view on screen stays. */
  const closeUnsupported = (report: Report, id: number) => {
    setUnsupportedIds((s) => new Set([...s, id]));
    closeReportKeepView();
    setReportNotice(`"${report.name}" was saved by a newer version of the console and cannot be opened here.`);
  };

  /**
   * The report landed: put it on screen as the opening intended and mark it
   * ready. A write that committed while the read was out (a rename the
   * reopening retired) is newer than its answer: that newer copy lands.
   */
  const landOpen = (fetched: Report, id: number, mode: OpenMode, push: boolean) => {
    const report = noteCopy(id, fetched, 'read')?.held ?? fetched;
    const decoded = decodeView(report.state, 'report');
    if (decoded.status === 'unsupported') {
      closeUnsupported(report, id);
      return;
    }
    const view = decoded.effective;
    if (mode === 'apply') applyView(view, id, push);
    else if (mode === 'layout') applyView(view, id, false, true);
    else if (mode === 'baseline+layout') {
      setColumnOrder(view.columnOrder);
      setHidden(new Set(view.hidden));
      setWidths(view.widths);
    }
    setOpen({ id, mode, push, status: 'ready', report, baseline: view, adjustments: decoded.adjustments, error: null });
    if (decoded.adjustments.length > 0) {
      setReportNotice(`Some of "${report.name}" could not be applied: ${decoded.adjustments.join('; ')}.`);
    }
  };

  const startOpen = (id: number, mode: OpenMode, opts: { push: boolean }) => {
    newOpening();
    followReport(id);
    // The outgoing view's editors are retired now, not when the report
    // lands: nothing of theirs writes a URL under the report being opened.
    resetEditorsToUrl();
    setOpen({ id, mode, push: opts.push, status: 'loading', report: null, baseline: null, adjustments: [], error: null });
    setReportNotice(null);
    setReportError(null);
    fetchOpenReport(id, {
      onReport: (report) => landOpen(report, id, mode, opts.push),
      onGone: reportGone,
      onError: (err) => setOpen((o) => (o && o.id === id ? { ...o, status: 'error', error: extractErrorMessage(err) } : o)),
    });
  };

  // The URL changed under the page. Its own writes (PAGE_URL_WRITE) change
  // nothing here: the report they install or close is already set, and an
  // edit to the view on screen while another report loads leaves that
  // loading alone. Everything else is navigation. Back and Forward (POP)
  // land on a URL that already holds its whole data view (`rv=1`, or an
  // unsaved view): the editors take what it says — search box, debounce,
  // pending chips — and nothing saved is re-applied, whether the landing
  // names another report, the same one at another URL, or none; it is a new
  // opening all the same, so the dialogs, confirmations and jobs of the view
  // left behind are retired. A link arriving while mounted opens what it
  // names as a first visit would. FS-900: landing on the same report while
  // its visibility is in question retires that check's read with the rest,
  // and asks again under the new opening, still only to check (an opening
  // whose read gave way to the check included: it is not loaded again).
  const handledLocationKey = useRef(location.key);
  useEffect(() => {
    if (handledLocationKey.current === location.key) return;
    handledLocationKey.current = location.key;
    if (navigationType !== 'POP' && isPageUrlWrite(location.state)) return;
    resetEditorsToUrl();
    const current = openRef.current;
    const landedId = reportIdFromParams(searchParams);
    if (landedId === null) {
      newOpening();
      followReport(null);
      setOpen(null);
      return;
    }
    const inQuestion = record.current?.id === landedId && record.current.visibility !== null;
    if (navigationType === 'POP' && current?.id === landedId && (current.status === 'ready' || inQuestion)) {
      newOpening();
      if (inQuestion) checkVisibility(landedId);
      return;
    }
    startOpen(
      landedId,
      hasReportViewMarker(searchParams) ? 'baseline+layout' : urlCarriesData(searchParams) ? 'layout' : 'apply',
      { push: false },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  /**
   * A confirmation the operator may answer after the page has moved on (the
   * account changed, Back was pressed, another report opened): the answer
   * counts only while the opening it was asked under is still the one on
   * screen.
   */
  const confirmWhileAlive = async (opts: ConfirmOptions): Promise<boolean> => {
    const lease = lifecycle.lease('read');
    try {
      return (await confirm({ ...opts, returnFocusTo: REPORT_TRIGGER_SELECTOR })) && lease.alive();
    } finally {
      lease.release();
    }
  };

  /** An adjusted load: Save and Share store what is on screen, so say what the saved report loses first. */
  const okToDropAdjustments = async (o: OpenReport & { report: Report }, verb: 'Save' | 'Share'): Promise<boolean> =>
    o.adjustments.length === 0 ||
    confirmWhileAlive({
      title: `${verb} "${o.report.name}"?`,
      message: `${verb === 'Share' ? 'Sharing saves the report, which' : 'Saving'} will remove: ${o.adjustments.join('; ')}.`,
      confirmLabel: verb,
      cancelLabel: 'Cancel',
    });

  /**
   * Choosing a report, the current one included, always applies it afresh.
   * Like Default view and Revert, it does not ask first: the changed dot on
   * the report menu already shows the view differs from the saved one.
   */
  const chooseReport = (id: number) => {
    startOpen(id, 'apply', { push: true });
  };

  const defaultView = () => {
    newOpening();
    followReport(null);
    setOpen(null);
    setReportNotice(null);
    applyView(defaultDeviceListView(), null, true);
  };

  /**
   * Revert is a new opening: a save still in flight answers for the old one.
   * It puts the baseline back; a visibility check it retired is asked again
   * under it, and only checks.
   */
  const revertReport = () => {
    const o = open;
    if (!o?.baseline) return;
    newOpening();
    applyView(o.baseline, o.id, true);
    if (record.current?.id === o.id && record.current.visibility) checkVisibility(o.id);
  };

  /** Blur the editors, wait out their debounce, then read the view on screen. */
  const settledView = async (): Promise<DeviceListView> => {
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
    await wait(FILTER_DEBOUNCE_MS);
    await wait(0);
    return viewRef.current;
  };

  /**
   * Rename, share, unshare and save answers, and those of writes the page
   * moved past: metadata and `updated_at`, never the view, and never back to
   * a copy older than, or as old as, one already held (`noteCopy`). A save
   * also advances the baseline unless its answer is older than the newest
   * copy: then it was saved over since. A reopening still loading keeps the
   * copy for when it lands.
   */
  const takeMetadata = (id: number, saved: Report, baseline?: DeviceListView) => {
    const copy = noteCopy(id, saved, 'write');
    if (!copy) return;
    setOpen((o) =>
      o && o.id === id && o.report
        ? { ...o, report: copy.held, ...(baseline && !copy.older ? { baseline, adjustments: [] } : {}) }
        : o,
    );
  };

  const openCreated = (report: Report) => {
    newOpening();
    followReport(report.id);
    noteCopy(report.id, report, 'write');
    const decoded = decodeView(report.state, 'report');
    setOpen({ id: report.id, mode: 'baseline', push: false, status: 'ready', report, baseline: decoded.effective, adjustments: [], error: null });
    writeUrl(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(REPORT_PARAM, String(report.id));
        next.set(REPORT_VIEW_PARAM, '1');
        return next;
      },
      { replace: true },
    );
    refreshList();
  };

  /**
   * A write retired by Revert or another opening still committed, and its
   * answer is the only way to learn what it did: when the same report is
   * still open (or being reopened), its metadata and token move forward to
   * the answer if it is newer than every copy held, and no error is shown.
   */
  const reconcileRetiredWrite = (id: number, saved: Report) => {
    takeMetadata(id, saved);
    refreshList();
  };

  /**
   * FS-900 — ask whether the open report is still there for this account:
   * one GET under the current opening, retiring any other read of it (an
   * opening still loading included: its answer may predate the write that
   * put the report in question, and an opening asked for before the
   * question arose is not applied any more). The answer only checks:
   *  - 200: the metadata and rights move forward (`noteCopy`). An opening
   *    that never landed becomes ready with the copy as its baseline, the
   *    view on screen staying exactly as it is (URL data, search box,
   *    filters, layout); the URL is marked as the report's, by replacement.
   *    A held baseline stays: Revert still puts back what it put back. A copy
   *    saved by a newer console closes the report, the view staying;
   *  - 404: the report closes over the view on screen, with the notice;
   *  - anything else: the question stays, and Retry asks again (`retryOpen`).
   *    A 403's refusal turns off the report's own actions, and stays through
   *    Retry until a read succeeds.
   * Back, Forward or Revert to the same report retire the read and ask again
   * under the new opening, still only to check; another report, the Default
   * view, an Open of the report chosen after the question arose, closing the
   * report or unmounting ends the question (`followReport`).
   */
  const checkVisibility = (id: number) => {
    const rec = record.current;
    if (rec?.id !== id) return;
    const refusal = rec.visibility?.refusal ?? null;
    setVisibility(id, { status: 'reading', error: null, refusal });
    setOpen((o) => (o && o.id === id && o.status === 'error' ? { ...o, status: 'loading', error: null } : o));
    fetchOpenReport(id, {
      onReport: (report) => landCheck(id, report),
      onGone: reportNoLongerSeen,
      onError: (err) => {
        const error = extractErrorMessage(err);
        setVisibility(id, { status: 'error', error, refusal: reportErrorStatus(err) === 403 ? error : refusal });
        setOpen((o) => (o && o.id === id && o.status !== 'ready' ? { ...o, status: 'error', error } : o));
      },
    });
  };

  const landCheck = (id: number, fetched: Report) => {
    const copy = noteCopy(id, fetched, 'read');
    const o = openRef.current;
    if (!copy || o?.id !== id) return;
    const decoded = decodeView(copy.held.state, 'report');
    if (decoded.status === 'unsupported') {
      closeUnsupported(copy.held, id);
      return;
    }
    setVisibility(id, null);
    if (o.baseline !== null) {
      setOpen((cur) => (cur && cur.id === id ? { ...cur, report: copy.held } : cur));
    } else {
      setOpen((cur) =>
        cur && cur.id === id
          ? {
              ...cur,
              mode: 'baseline',
              status: 'ready',
              report: copy.held,
              baseline: cur.baseline ?? decoded.effective,
              adjustments: cur.baseline ? cur.adjustments : decoded.adjustments,
              error: null,
            }
          : cur,
      );
      const now = latestUrl.current.searchParams;
      if (reportIdFromParams(now) !== id || !hasReportViewMarker(now)) {
        writeUrl(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set(REPORT_PARAM, String(id));
            next.set(REPORT_VIEW_PARAM, '1');
            return next;
          },
          { replace: true },
        );
      }
    }
    refreshList();
  };

  /**
   * A write's answer the page cannot take at its word (retired and saying
   * the report is out of sight, or answered under a token the account no
   * longer holds): a fresh read decides while the report is the open one;
   * otherwise only the list is refreshed.
   */
  const recheck = (id: number) => {
    if (record.current?.id === id) checkVisibility(id);
    else refreshList();
  };

  /**
   * A write answered that the caller no longer sees the report: `null` (an
   * admin unshared someone else's), or a 404. Answered for the opening on
   * screen, a null is the latest word: the report closes and the view stays.
   * Retired first — by Revert, Back or Forward, or the report being chosen
   * again — the answer may be older than a read that landed or is in flight
   * (the owner shared it again; a reopening that read the row before the
   * unshare committed), so the page does not close on it but asks once more
   * (`checkVisibility`).
   */
  const noLongerSeen = (id: number, retired: boolean) => {
    if (!retired && record.current?.id === id) reportNoLongerSeen();
    else recheck(id);
  };

  /**
   * One PATCH, every answer handled in one place. The lease says whether the
   * answer may still change the page:
   *  - alive: `null` means the caller no longer sees the report (an admin
   *    unshared someone else's) — it closes, the view stays; `stale` opens the
   *    reload dialog; `not_found` closes it like a read's 404; anything else
   *    is thrown for the caller's banner;
   *  - retired by Revert or another opening: `null` and `not_found` are
   *    checked with a fresh read before anything closes (`noLongerSeen`); a
   *    report is reconciled as above;
   *  - answered under a token the account no longer holds (alive or not):
   *    nothing in it is taken — not its rights, not a null's or a 404's word
   *    that the report is out of sight; a fresh read decides (`recheck`).
   */
  const patchReport = async (
    lease: Lease,
    report: Report,
    body: Omit<UpdateReportBody, 'expected_updated_at'>,
    savesState: boolean,
  ): Promise<WriteOutcome> => {
    const askedUnder = storedToken();
    try {
      const result = await updateReport(report.id, { expected_updated_at: report.updated_at, ...body }, lease.signal);
      // The page is gone (or another account's): nothing of this answer is looked at.
      if (!lease.mounted()) return 'retired';
      const alive = lease.alive();
      if (storedToken() !== askedUnder) {
        recheck(report.id);
        return alive ? 'done' : 'retired';
      }
      if (result.report === null) {
        noLongerSeen(report.id, !alive);
        return alive ? 'gone' : 'retired';
      }
      if (!alive) {
        reconcileRetiredWrite(report.id, result.report);
        return 'retired';
      }
      takeMetadata(report.id, result.report, savesState ? decodeView(result.report.state, 'report').effective : undefined);
      refreshList();
      return 'done';
    } catch (err) {
      if (isAbortError(err) || !lease.mounted()) return 'retired';
      if (!lease.alive()) {
        if (isNotFound(err)) recheck(report.id);
        return 'retired';
      }
      if (reportErrorCode(err) === 'stale') {
        setDialog({ kind: 'stale', report });
        return 'stale';
      }
      if (isNotFound(err)) {
        if (storedToken() === askedUnder) {
          reportGone();
          return 'gone';
        }
        // That token's 404: checked under the current one, and said in the form meanwhile.
        recheck(report.id);
      }
      throw err;
    }
  };

  const saveState = async (report: Report, view: DeviceListView, confirmShare: boolean): Promise<WriteOutcome> => {
    const lease = lifecycle.lease('write');
    try {
      return await patchReport(
        lease,
        report,
        { state: toReportState(view), ...(confirmShare ? { confirm_share: true as const } : {}) },
        true,
      );
    } finally {
      lease.release();
    }
  };

  const saveReport = async () => {
    const o = open;
    if (!o?.report || o.status !== 'ready' || !o.report.can_edit) return;
    const lease = lifecycle.lease('read');
    try {
      if (!(await okToDropAdjustments({ ...o, report: o.report }, 'Save'))) return;
      const view = await settledView();
      if (!lease.alive() || openRef.current?.id !== o.id) return;
      if (o.report.shared_scope !== null) {
        setDialog({ kind: 'saveShared', report: o.report, view });
        return;
      }
      await saveState(o.report, view, false);
    } catch (err) {
      setReportError(`Could not save the report: ${extractErrorMessage(err)}`);
    } finally {
      lease.release();
    }
  };

  const openNameDialog = async (purpose: 'create' | 'copy' | 'rename') => {
    // Nothing is captured from a view that is still being resolved.
    if (gatingRef.current) return;
    if (purpose !== 'create' && openRef.current?.status !== 'ready') return;
    const lease = lifecycle.lease('read');
    try {
      const view = purpose === 'rename' ? viewRef.current : await settledView();
      if (!lease.alive()) return;
      setDialog({ kind: 'name', purpose, view });
    } finally {
      lease.release();
    }
  };

  const submitName = async (purpose: 'create' | 'copy' | 'rename', view: DeviceListView, name: string): Promise<WriteOutcome> => {
    const lease = lifecycle.lease('write');
    try {
      if (purpose === 'rename') {
        const report = openRef.current?.report;
        if (!report) return 'retired';
        return await patchReport(lease, report, { name }, false);
      }
      const created = await createReport({ name, state: toReportState(view), shared_scope: null }, lease.signal);
      if (!lease.alive()) {
        // Created all the same: it shows in the list, but the page has moved on.
        if (lease.mounted()) refreshList();
        return 'retired';
      }
      openCreated(created);
      return 'done';
    } finally {
      lease.release();
    }
  };

  /**
   * Share previews the exact candidate that will be stored. The working view
   * when Modified (saved together with the share); else the saved state —
   * and when that differs from what the server holds (an adjusted load, or a
   * retired save that committed), it is saved with the share too, so the
   * audience sees what was previewed and nothing else.
   */
  const openShareDialog = async () => {
    const o = open;
    if (!o?.report || o.status !== 'ready' || !o.baseline) return;
    const lease = lifecycle.lease('read');
    try {
      if (!(await okToDropAdjustments({ ...o, report: o.report }, 'Share'))) return;
      const view = modified ? await settledView() : o.baseline;
      if (!lease.alive() || openRef.current?.id !== o.id) return;
      const working = viewsDiffer(view, o.baseline);
      const savedDiffers = viewsDiffer(o.baseline, decodeView(o.report.state, 'report').effective);
      setDialog({
        kind: 'share',
        report: o.report,
        candidate: view,
        candidateIsWorkingView: working,
        savesState: working || o.adjustments.length > 0 || savedDiffers,
      });
    } finally {
      lease.release();
    }
  };

  const submitShare = async (
    report: Report,
    candidate: DeviceListView,
    savesState: boolean,
    args: { shared_scope: string; name?: string },
  ): Promise<WriteOutcome> => {
    const lease = lifecycle.lease('write');
    try {
      return await patchReport(
        lease,
        report,
        {
          shared_scope: args.shared_scope,
          ...(args.name !== undefined ? { name: args.name } : {}),
          ...(savesState ? { state: toReportState(candidate) } : {}),
          confirm_share: true,
        },
        savesState,
      );
    } finally {
      lease.release();
    }
  };

  const unshareReport = async () => {
    const o = open;
    if (!o?.report || !o.report.can_unshare) return;
    const owner = o.report.is_mine ? null : (o.report.owner_email ?? 'a deleted account');
    const lease = lifecycle.lease('write');
    try {
      const ok = await confirm({
        title: `Stop sharing "${o.report.name}"?`,
        message: owner
          ? `The report belongs to ${owner}. Everyone at ${o.report.shared_scope} loses it, and it goes back to the owner's private list.`
          : `Everyone at ${o.report.shared_scope} loses it; it goes back to your private list.`,
        confirmLabel: 'Unshare',
        cancelLabel: 'Cancel',
        returnFocusTo: REPORT_TRIGGER_SELECTOR,
      });
      if (!ok || !lease.alive()) return;
      await patchReport(lease, o.report, { shared_scope: null }, false);
    } catch (err) {
      setReportError(`Could not unshare the report: ${extractErrorMessage(err)}`);
    } finally {
      lease.release();
    }
  };

  const deleteOpenReport = async () => {
    const o = open;
    if (!o?.report || !o.report.can_delete) return;
    const owner = o.report.is_mine ? null : (o.report.owner_email ?? 'a deleted account');
    const parts: string[] = [];
    if (owner) parts.push(`The report belongs to ${owner}.`);
    if (o.report.shared_scope) parts.push(`Everyone at ${o.report.shared_scope} loses it.`);
    parts.push('The view on screen stays, as an unsaved view. This cannot be undone.');
    const lease = lifecycle.lease('write');
    // The token the DELETE goes out under, taken once it is confirmed.
    let askedUnder: string | null = null;
    try {
      const ok = await confirm({
        title: `Delete "${o.report.name}"?`,
        message: parts.join(' '),
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        tone: 'danger',
        returnFocusTo: REPORT_TRIGGER_SELECTOR,
      });
      if (!ok || !lease.alive()) return;
      askedUnder = storedToken();
      await deleteReport(o.report.id, o.report.updated_at, lease.signal);
      // Deleted whether or not the opening moved on meanwhile: if the same
      // report is still the open one, it is gone. Answered under a token the
      // account no longer holds, a fresh read says so first.
      if (!lease.mounted()) return;
      if (storedToken() !== askedUnder) {
        recheck(o.report.id);
        return;
      }
      if (openRef.current?.id === o.report.id) closeReportKeepView();
      refreshList();
    } catch (err) {
      if (isAbortError(err)) return;
      // Answered 404 after the page moved on: checked, as a retired PATCH is.
      if (!lease.alive()) {
        if (lease.mounted() && isNotFound(err)) recheck(o.report.id);
        return;
      }
      if (reportErrorCode(err) === 'stale') {
        setDialog({ kind: 'stale', report: o.report });
        return;
      }
      if (isNotFound(err)) {
        if (storedToken() === askedUnder) {
          reportGone();
          return;
        }
        recheck(o.report.id);
      }
      setReportError(`Could not delete the report: ${extractErrorMessage(err)}`);
    } finally {
      lease.release();
    }
  };

  const reloadSaved = () => {
    const id = openRef.current?.id;
    setDialog(null);
    if (id !== undefined) startOpen(id, 'apply', { push: false });
  };

  /**
   * Retry what failed: the visibility check when that is what is in question
   * (it only checks again; nothing of the view on screen is reset), else the
   * opening as it was asked for.
   */
  const retryOpen = () => {
    const o = openRef.current;
    if (!o) return;
    if (record.current?.id === o.id && record.current.visibility) checkVisibility(o.id);
    else startOpen(o.id, o.mode, { push: o.push });
  };

  const myReportNames = (reports ?? []).filter((r) => r.is_mine).map((r) => r.name);

  // ---------------------------------------------------------------------
  // URL param helpers
  // ---------------------------------------------------------------------
  const setParam = (key: string, value: string | null, resetPage = true) => {
    writeUrl((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (resetPage) next.delete('page');
      return next;
    });
  };
  const goToPage = (p: number) => setParam('page', String(Math.min(Math.max(p, 1), pageCount)), false);

  // ---------------------------------------------------------------------
  // Typed filters (Plan 101 §3) — `pending` holds keys the user has picked
  // whose editors have no value yet (they live only in client state; the URL
  // never carries empty f.* params).
  // ---------------------------------------------------------------------
  const [pendingFilters, setPendingFilters] = useState<string[]>([]);
  const urlFilterKeys = Object.keys(filters);
  const activeFilterKeys = [...urlFilterKeys, ...pendingFilters.filter((k) => !(k in filters))];

  const changeFilter = (fk: string, value: string) => {
    if (value) {
      setParam(`f.${fk}`, value);
    } else {
      setParam(`f.${fk}`, null);
      setPendingFilters((p) => (p.includes(fk) ? p : [...p, fk]));
    }
  };
  const removeFilter = (fk: string) => {
    setPendingFilters((p) => p.filter((k) => k !== fk));
    if (fk in filters) setParam(`f.${fk}`, null);
  };
  const pickFilter = (fk: string, active: boolean) => {
    if (active) {
      if (!(fk in filters)) setPendingFilters((p) => (p.includes(fk) ? p : [...p, fk]));
    } else {
      removeFilter(fk);
    }
  };
  /**
   * The funnel in a column header: add that column's filter, then put the
   * cursor in its editor once the row has drawn it.
   */
  const [focusFilterKey, setFocusFilterKey] = useState<string | null>(null);
  const addFilterFromHeader = (fk: string) => {
    pickFilter(fk, true);
    setFocusFilterKey(fk);
  };
  useEffect(() => {
    if (!focusFilterKey) return;
    const chip = document.querySelector(`[data-filter-key="${CSS.escape(focusFilterKey)}"]`);
    const field = chip?.querySelector<HTMLElement>('input, select, button');
    field?.focus();
    setFocusFilterKey(null);
  }, [focusFilterKey]);
  const clearAllFilters = () => {
    setPendingFilters([]);
    writeUrl((prev) => {
      const next = new URLSearchParams(prev);
      for (const k of Object.keys(filtersFromSearchParams(prev))) next.delete(`f.${k}`);
      next.delete('page');
      return next;
    });
  };

  // ---------------------------------------------------------------------
  // Sorting (Plan 101 §4) — plain click makes the column the only sort and
  // cycles defaultSortDir → opposite → off; Shift+click appends it as the
  // next level (or cycles/removes it within the stack). Max 3 levels; the
  // server always appends the devices.id tiebreak.
  // ---------------------------------------------------------------------
  const applySortLevels = (levels: SortLevel[]) => {
    writeUrl((prev) => {
      const next = new URLSearchParams(prev);
      const s = serializeSort(levels);
      if (s) next.set('sort', s);
      else next.delete('sort');
      next.delete('dir'); // legacy pair superseded by the serialized stack
      next.delete('page');
      return next;
    });
  };

  const suppressNextHeaderClick = useRef(false);

  const onHeaderClick = (col: ColumnDef, e: React.MouseEvent) => {
    if (!col.sortable) return;
    if (suppressNextHeaderClick.current) return; // a drag just ended — never toggle sort
    const key = sortKeyOf(col);
    const idx = sortLevels.findIndex((l) => l.key === key);
    const opposite: SortDir = col.defaultSortDir === 'asc' ? 'desc' : 'asc';

    let next: SortLevel[];
    if (e.shiftKey) {
      if (idx === -1) {
        next =
          sortLevels.length >= MAX_SORT_LEVELS
            ? sortLevels
            : [...sortLevels, { key, dir: col.defaultSortDir }];
      } else if (sortLevels[idx].dir === col.defaultSortDir) {
        next = sortLevels.map((l, i) => (i === idx ? { key, dir: opposite } : l));
      } else {
        next = sortLevels.filter((_, i) => i !== idx);
      }
    } else if (idx === -1 || sortLevels.length > 1) {
      next = [{ key, dir: col.defaultSortDir }];
    } else if (sortLevels[idx].dir === col.defaultSortDir) {
      next = [{ key, dir: opposite }];
    } else {
      next = [];
    }
    applySortLevels(next);
  };

  const removeSortLevel = (key: string) => applySortLevels(sortLevels.filter((l) => l.key !== key));

  // ---------------------------------------------------------------------
  // Column layout (Plan 101 §5) — visibility, HTML5-DnD reorder, pointer
  // resize. All client-only state persisted via prefs.
  // ---------------------------------------------------------------------
  const visibleColumns = columnOrder
    .filter((k) => !hidden.has(k))
    .map((k) => COLUMN_BY_KEY[k])
    .filter(Boolean) as ColumnDef[];

  const widthOf = (c: ColumnDef) => Math.max(c.minWidth ?? DEFAULT_MIN_WIDTH, widths[c.key] ?? c.defaultWidth);
  const totalWidth =
    SELECT_COL_WIDTH +
    visibleColumns.reduce((sum, c) => sum + widthOf(c), 0) +
    (canWrite ? TRAILING_WIDTHS.actions : 0);

  const toggleColumn = (key: string, visible: boolean) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (visible) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const resetLayout = () => {
    const d = defaultPrefs();
    setColumnOrder(d.columnOrder);
    setHidden(new Set(d.hidden));
    setWidths({});
  };

  // --- drag-reorder (native HTML5 DnD on the <th>s; no library) ---
  const dragKey = useRef<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ key: string; side: 'left' | 'right' } | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const moveColumn = (dragged: string, target: string, side: 'left' | 'right') => {
    if (dragged === target) return;
    setColumnOrder((order) => {
      const next = order.filter((k) => k !== dragged);
      const idx = next.indexOf(target);
      if (idx === -1) return order;
      next.splice(side === 'right' ? idx + 1 : idx, 0, dragged);
      return next;
    });
  };

  const onHeaderDragStart = (col: ColumnDef) => (e: React.DragEvent) => {
    dragKey.current = col.key;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', col.key);
  };
  const onHeaderDragOver = (col: ColumnDef) => (e: React.DragEvent<HTMLTableCellElement>) => {
    if (!dragKey.current || dragKey.current === col.key) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const side: 'left' | 'right' = e.clientX < rect.left + rect.width / 2 ? 'left' : 'right';
    setDropTarget((cur) => (cur?.key === col.key && cur.side === side ? cur : { key: col.key, side }));
  };
  const endDrag = () => {
    dragKey.current = null;
    setDropTarget(null);
    // A completed drag normally never fires `click`, but don't rely on it —
    // suppress the next header click so a drop can't toggle sort in browser
    // edge cases. Cleared on the next tick.
    suppressNextHeaderClick.current = true;
    setTimeout(() => {
      suppressNextHeaderClick.current = false;
    }, 0);
  };
  const onHeaderDrop = (col: ColumnDef) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragKey.current && dropTarget?.key === col.key) {
      moveColumn(dragKey.current, col.key, dropTarget.side);
    }
    endDrag();
  };

  // Keyboard alternative (a11y): Alt+←/→ on a focused header moves the column
  // among the visible ones; announced via the live region.
  const onHeaderKeyDown = (col: ColumnDef) => (e: React.KeyboardEvent) => {
    if (!e.altKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
    e.preventDefault();
    const delta = e.key === 'ArrowLeft' ? -1 : 1;
    const vis = visibleColumns.map((c) => c.key);
    const from = vis.indexOf(col.key);
    const to = from + delta;
    if (from === -1 || to < 0 || to >= vis.length) return;
    moveColumn(col.key, vis[to], delta === 1 ? 'right' : 'left');
    setAnnouncement(`${col.label} column moved ${delta === 1 ? 'right' : 'left'}`);
  };

  // --- drag-resize (pointer events on a 6px handle; can't collide with DnD) ---
  const startResize = (col: ColumnDef) => (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widthOf(col);
    const min = col.minWidth ?? DEFAULT_MIN_WIDTH;
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    const onMove = (ev: PointerEvent) => {
      const w = Math.max(min, Math.round(startW + (ev.clientX - startX)));
      setWidths((prev) => (prev[col.key] === w ? prev : { ...prev, [col.key]: w }));
    };
    const onUp = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  };
  const resetWidth = (key: string) => {
    setWidths((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // ---------------------------------------------------------------------
  // Edit modal (this page's original role, kept intact). The device is
  // fetched fresh by id when the modal opens — the search row is a display
  // projection, not the editable Device record.
  // ---------------------------------------------------------------------
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  // FS-890: the device as it was loaded. Save is for a real change, and the
  // path is sent only when the operator changed it.
  const [editBaseline, setEditBaseline] = useState<Device | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editLoadingId, setEditLoadingId] = useState<number | null>(null);
  const [editError, setEditError] = useState('');

  const closeEdit = () => {
    setSelectedDevice(null);
    setEditBaseline(null);
  };

  const handleEdit = async (row: DeviceSearchRow) => {
    setEditLoadingId(row.id);
    setEditError('');
    try {
      const res = await axiosInstance.get<Device>(`${config.devicesApiUrl}/devices/${row.id}`);
      setSelectedDevice(res.data);
      setEditBaseline(res.data);
    } catch (error) {
      console.error('Error loading device for edit:', error);
    } finally {
      setEditLoadingId(null);
    }
  };

  const handleSave = async () => {
    if (selectedDevice && editBaseline) {
      setEditError('');
      if (entityPathChanged(selectedDevice, editBaseline)) {
        const problem = validateEntityPath(selectedDevice.entity_path ?? '', scopes);
        if (problem) {
          setEditError(problem);
          return;
        }
      }
      setEditSaving(true);
      try {
        await axiosInstance.put(
          `${config.devicesApiUrl}/devices/${selectedDevice.id}`,
          deviceUpdateBody(selectedDevice, editBaseline),
        );
        closeEdit();
        refetch();
      } catch (error: any) {
        console.error('Error saving device:', error);
        // Keep the modal open and say why. The filter threshold rules (FS-443)
        // can only be checked server-side, so a rejected save has to explain
        // itself here or the operator just sees the value revert.
        setEditError(error?.response?.data?.message || 'Failed to save device.');
      } finally {
        setEditSaving(false);
      }
    }
  };

  const changeEdit = (patch: Partial<Device>) => {
    setSelectedDevice((current) => (current ? { ...current, ...patch } : current));
  };

  const isEnriched = (deviceName: string): boolean => deviceName in enrichmentMap;

  // Source cell (registry column 'source'): page-supplied because it reads
  // the live Firestore enrichment map, which registry row renders can't see.
  const sourceCell = (deviceName: string) =>
    isEnriched(deviceName) ? (
      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-sm border border-status-success-border bg-status-success text-status-success-foreground">
        App
      </span>
    ) : (
      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-sm border border-status-neutral-border bg-status-neutral text-status-neutral-foreground">
        Manual
      </span>
    );

  // Row click → device workspace. Ignore clicks on interactive elements
  // (Edit button, filter chips, links) and clicks that end a text selection
  // (copying a serial must not navigate). Cmd/Ctrl+click opens a new tab.
  const openDevice = (id: number, e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button,a,input,select,label')) return;
    if (window.getSelection()?.toString()) return;
    const url = `/devices/${id}`;
    if (e.metaKey || e.ctrlKey) window.open(url, '_blank');
    else navigate(url);
  };

  const selectedEnrichment = selectedDevice ? enrichmentMap[selectedDevice.name] : undefined;

  // FS-890/FS-911: why the Edit modal's Save is off, or null when it is on. A
  // changed path must be a path inside the caller's scope; an unchanged one is
  // not sent, so it is not checked. A permission lost while the modal is open
  // turns Save off too.
  const editSaveBlocked = (() => {
    if (!selectedDevice || !editBaseline) return null;
    if (!canWrite) return 'Saving a device needs the write_devices permission.';
    if (editSaving) return 'Saving the changes to this device…';
    if (!deviceDraftChanged(selectedDevice, editBaseline)) return 'Nothing to save: no field has changed.';
    return null;
  })();

  // FS-745 — download every matching device, not just this page, in the
  // columns on screen. The list is re-read in 200-row pages with the same
  // search, filters and sort, so the file is the whole list in table order.
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const downloadListCsv = async () => {
    if (gatingRef.current) return; // the view is not resolved yet (FS-889)
    // FS-889: the walk belongs to this account and opening; another report,
    // Revert or another account stops it, and nothing it fetched is saved.
    const lease = lifecycle.lease('read');
    setCsvBusy(true);
    setCsvError(null);
    try {
      const all = await fetchAllDeviceRows(params, undefined, undefined, lease.signal);
      if (!lease.alive()) return;
      const csv = deviceListCsv(all, visibleColumns, {
        source: (r) => (isEnriched(r.name) ? 'App' : 'Manual'),
      });
      downloadCsv(deviceListCsvFilename(), csv);
    } catch (err) {
      if (isAbortError(err) || !lease.alive()) return;
      setCsvError(`Download failed: ${extractErrorMessage(err)}`);
    } finally {
      lease.release();
      setCsvBusy(false);
    }
  };

  // FS-882 — selection. A set of device ids (never row indexes), so it
  // survives paging, sorting and filtering, kept in sessionStorage. The view
  // key is the list without its page: positions in it decide the order Open
  // in Devices hands over.
  const selection = useDeviceSelection({
    userId,
    pageIds: rows.map((r) => r.id),
    pageOffset: (page - 1) * pageSize,
    viewKey: JSON.stringify({ q: params.q ?? '', filters, sort: sortStr }),
    // Not `!loading`: that lags the params by a render, during which the old
    // page's rows would be recorded at the new page's offsets. Never while a
    // report is still loading (the hook reports no rows then anyway).
    settled: rowsCurrent && !gating,
  });

  // Cmd/Ctrl+click opens the Devices page in a new tab, like a row click.
  const openInDevices = (e: React.MouseEvent) => {
    const url = devicesHrefForIds(selection.orderedIds().slice(0, OPEN_IN_DEVICES_CAP));
    if (e.metaKey || e.ctrlKey) window.open(url, '_blank');
    else navigate(url);
  };

  // A click anywhere in the checkbox cell toggles the row (a bigger target
  // than the box) and never reaches the row's open-the-device handler.
  const onSelectCellClick = (id: number) => (e: React.MouseEvent<HTMLTableCellElement>) => {
    e.stopPropagation();
    selection.toggleRow(id, e.shiftKey);
  };
  const onSelectBoxClick = (id: number) => (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation(); // the cell's handler would toggle it back
    selection.toggleRow(id, e.shiftKey);
  };
  // Shift+click must not select the text of the rows in between.
  const noShiftTextSelect = (e: React.MouseEvent) => {
    if (e.shiftKey) e.preventDefault();
  };

  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  const primarySortKey = sortLevels[0]?.key;

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="text-2xl font-bold">Device List</h1>
          <a href={CLIP_LIFECYCLE_DOC_URL} target="_blank" rel="noopener noreferrer"
            title={CLIP_LIFECYCLE_DOC_TITLE} className="text-sm text-link hover:underline">
            Clip lifecycle guide
          </a>
        </div>
        <div className="flex min-w-0 grow flex-wrap items-center justify-end gap-2">
          <ReportMenu
            open={
              open
                ? { id: open.id, report: open.report, loading: open.status === 'loading', resolved: open.status === 'ready' }
                : null
            }
            modified={modified}
            nothingToSave={nothingToSave}
            refusal={
              visibility?.refusal
                ? `The server refused to read this report (${visibility.refusal}), so it would refuse this too. Retry asks again.`
                : null
            }
            reports={reports}
            listError={listError}
            unsupportedIds={unsupportedIds}
            onRetryList={refreshList}
            onDefaultView={defaultView}
            onOpenReport={chooseReport}
            onSaveNew={() => void openNameDialog('create')}
            onSave={() => void saveReport()}
            onSaveCopy={() => void openNameDialog('copy')}
            onRename={() => void openNameDialog('rename')}
            onShare={() => void openShareDialog()}
            onUnshare={() => void unshareReport()}
            onRevert={revertReport}
            onDelete={() => void deleteOpenReport()}
          />
          <div className="flex min-w-0 max-w-md grow basis-64 flex-col gap-1">
            <label htmlFor="device-list-search" className="sr-only">Search devices</label>
            <Input
              id="device-list-search"
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onBlur={commitQNow}
              placeholder="Search serial, id, description, owner, email, location, path…"
              title="Matches serial, id, description, owner, email, location or path. A list of ids separated by commas or spaces (214, 230 352) matches those device ids only, in the order given unless a column is sorted."
              aria-label="Search devices"
              aria-describedby="device-list-search-help"
            />
            <p id="device-list-search-help" className="text-xs text-muted-foreground">
              Search serial, ID, description, owner, email, location or path.
            </p>
          </div>
          <FilterPicker activeKeys={activeFilterKeys} onPick={pickFilter} />
          <ColumnChooser
            columnOrder={columnOrder}
            hidden={hidden}
            onToggle={toggleColumn}
            onReset={resetLayout}
          />
          <Button
            variant="secondary"
            onClick={downloadListCsv}
            disabled={csvBusy || total === 0 || gating}
            title={
              gating
                ? 'The report is still loading; the download is available once it is applied'
                : csvBusy
                  ? 'Building the CSV…'
                  : `Download all ${total} matching device${total === 1 ? '' : 's'} as CSV, in the columns and order shown (${deviceListCsvFilename()})`
            }
            aria-label="Download device list as CSV"
          >
            <Download size={18} aria-hidden />
          </Button>
        </div>
      </div>

      {/* Active typed filters. Keyed by the application generation (FS-889)
          so a debounced editor from the previous view is unmounted, timer
          and all, when a report, Revert or Default view puts a new one up. */}
      <ActiveFilterRow
        key={editorsGen}
        activeKeys={activeFilterKeys}
        values={filters}
        facets={facets}
        onChange={changeFilter}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      {/* FS-889: a report that could not be opened as saved, or at all. */}
      {reportNotice && (
        <div
          className="mt-2 flex items-start gap-2 p-2 rounded-sm border border-status-warning-border bg-amber-50 text-xs text-amber-900"
          data-testid="report-notice"
        >
          <span className="flex-1">{reportNotice}</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setReportNotice(null)}
            className="shrink-0"
            aria-label="Dismiss report notice"
            title="Hide this notice"
          >
            Dismiss
          </Button>
        </div>
      )}
      {open?.status === 'error' && (
        <div
          className="mt-2 flex items-start gap-2 p-2 rounded-sm border border-status-error-border bg-red-100 text-xs text-red-800"
          role="alert"
          data-testid="report-load-error"
        >
          <span className="flex-1">Could not load the report: {open.error}</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={retryOpen}
            className="shrink-0"
            title="Ask the server for the report again"
          >
            Retry
          </Button>
        </div>
      )}
      {/* FS-900: the open report's visibility could not be checked. No
          Dismiss: the question stays until the server answers it. */}
      {open?.status === 'ready' && visibility?.status === 'error' && (
        <div
          className="mt-2 flex items-start gap-2 p-2 rounded-sm border border-status-error-border bg-red-100 text-xs text-red-800"
          role="alert"
          data-testid="report-check-error"
        >
          <span className="flex-1">
            Could not check that you still see this report: {visibility.error}. The view on screen stays as it is.
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={retryOpen}
            className="shrink-0"
            title="Ask the server again whether this report is still there for you; nothing on screen changes"
          >
            Retry
          </Button>
        </div>
      )}
      {reportError && (
        <div
          className="mt-2 flex items-start gap-2 p-2 rounded-sm border border-status-error-border bg-red-100 text-xs text-red-800"
          role="alert"
          data-testid="report-error"
        >
          <span className="flex-1">{reportError}</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setReportError(null)}
            className="shrink-0"
            aria-label="Dismiss report error"
            title="Hide this message"
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* FS-501: a tag in a saved view was deleted, not merged, so there was
          nowhere to carry the filter across. Said plainly, because a Not
          Tagged filter that loses its last value quietly stops hiding
          anything. */}
      {goneTagFilters.length > 0 && (
        <div className="mt-2 flex items-start gap-2 p-2 rounded-sm border border-status-warning-border bg-amber-50 text-xs text-amber-900">
          <span className="flex-1">
            {goneTagFilters.length === 1 ? 'A tag' : `${goneTagFilters.length} tags`} in this
            view {goneTagFilters.length === 1 ? 'has' : 'have'} been deleted, so{' '}
            {goneTagFilters.length === 1 ? 'it is' : 'they are'} no longer filtering anything.
            Devices that were hidden by {goneTagFilters.length === 1 ? 'it' : 'them'} are now
            shown.
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setGoneTagFilters([])}
            className="shrink-0"
            aria-label="Dismiss deleted-tag notice"
            title="Hide this notice. The deleted tags' filters are already gone from the list."
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Sort readout chips (discoverability for Shift+click) */}
      {sortLevels.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
          <span>Sorted by:</span>
          {sortLevels.map((l, i) => (
            <span
              key={l.key}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-gray-100 border"
            >
              {sortLabel(l.key)} {l.dir === 'asc' ? '↑' : '↓'}
              {sortLevels.length > 1 && <sup>{i + 1}</sup>}
              <Button
                variant="icon"
                size="sm"
                onClick={() => removeSortLevel(l.key)}
                className="ml-0.5"
                aria-label={`Remove ${sortLabel(l.key)} sort`}
                title={`Stop sorting by ${sortLabel(l.key)}; the other sort levels stay`}
              >
                <X size={12} aria-hidden />
              </Button>
            </span>
          ))}
        </div>
      )}

      {/* FS-882: shown only while something is selected; outside the table
          so a filter that hides every selected row still shows the bar. */}
      <SelectionBar
        count={selection.selected.size}
        notShown={selection.notShown}
        onOpen={openInDevices}
        onClear={selection.clear}
        pageRowCount={rows.length}
        matchingTotal={total}
        pageAllSelected={rows.length > 0 && selection.headerState === 'all'}
        allMatchingSelected={selection.allMatchingSelected}
        allMatchingCount={selection.allMatchingCount}
        matchingBusy={selection.matchingBusy}
        matchingError={selection.matchingError}
        onSelectAllMatching={() => {
          if (gatingRef.current) return; // the view is not resolved yet (FS-889)
          // The walk stops with its opening (see downloadListCsv), and what
          // it found is applied only while that opening is still on screen.
          const lease = lifecycle.lease('read');
          void selection
            .selectAllMatching(() => fetchAllMatchingIds(params, undefined, lease.signal), () => lease.alive())
            .finally(() => lease.release());
        }}
      />

      {error && <p className="mt-4 text-status-error-foreground">{error}</p>}
      {csvError && (
        <p role="alert" className="mt-2 text-status-error-foreground text-sm">
          {csvError}
        </p>
      )}
      {loading && rows.length === 0 && <p className="mt-4 text-muted-foreground">Loading...</p>}
      {enrichmentLoading && !loading && (
        <p className="mt-1 text-muted-foreground text-sm">Loading app data...</p>
      )}
      {!loading && rows.length === 0 && !error && (
        <p className="mt-4 text-muted-foreground">No devices match. Try clearing filters or a different search.</p>
      )}

      <span role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>

      {rows.length > 0 && (
        <div className="mt-4">
          <div className="overflow-x-auto">
            <table
              className="text-sm"
              style={{ tableLayout: 'fixed', width: totalWidth, minWidth: '100%' }}
              data-testid="fleet-table"
            >
              <colgroup>
                <col style={{ width: SELECT_COL_WIDTH }} />
                {visibleColumns.map((col) => (
                  <col key={col.key} data-colkey={col.key} style={{ width: widthOf(col) }} />
                ))}
                {canWrite && <col data-colkey="actions" style={{ width: TRAILING_WIDTHS.actions }} />}
              </colgroup>
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  {/* FS-882: mixed is the native indeterminate state, set
                      through the ref (it has no attribute). */}
                  <th scope="col" data-select-col className="py-2 align-top">
                    <div className="flex justify-center pt-2">
                      <label id="device-list-select-page-label" htmlFor="device-list-select-page" className="sr-only">Select all {rows.length} devices on this page</label>
                      <Checkbox
                        id="device-list-select-page"
                        checked={selection.headerState === 'all'}
                        indeterminate={selection.headerState === 'some'}
                        onCheckedChange={selection.toggleHeader}
                        aria-labelledby="device-list-select-page-label"
                        title={
                          selection.headerState === 'none'
                            ? `Select all ${rows.length} devices on this page`
                            : 'Unselect every device on this page. Devices selected that are not on this page stay selected.'
                        }
                        className="cursor-pointer"
                      />
                    </div>
                  </th>
                  {visibleColumns.map((col) => {
                    const key = sortKeyOf(col);
                    const level = sortLevels.findIndex((l) => l.key === key);
                    const active = col.sortable && level !== -1;
                    const dir = active ? sortLevels[level].dir : undefined;
                    const ariaSort: 'none' | 'ascending' | 'descending' =
                      active && key === primarySortKey
                        ? dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none';
                    return (
                      <th
                        key={col.key}
                        scope="col"
                        data-colkey={col.key}
                        aria-sort={col.sortable ? ariaSort : undefined}
                        className="relative py-2 pr-1 font-medium group align-top"
                        draggable
                        onDragStart={onHeaderDragStart(col)}
                        onDragOver={onHeaderDragOver(col)}
                        onDrop={onHeaderDrop(col)}
                        onDragEnd={endDrag}
                      >
                        {/* insertion indicator */}
                        {dropTarget?.key === col.key && (
                          <span
                            aria-hidden
                            className={`absolute top-0 h-full w-0.5 bg-blue-500 ${
                              dropTarget.side === 'left' ? 'left-0' : 'right-0'
                            }`}
                          />
                        )}
                        <div className="flex min-w-0 items-start">
                          {/* The operator sets the column width, so a long
                              label wraps inside it rather than staying on one line. */}
                          <Button
                            variant="secondary"
                            onClick={(e) => onHeaderClick(col, e)}
                            onMouseDown={(e) => {
                              if (e.shiftKey) e.preventDefault(); // no text selection on Shift+click
                            }}
                            onKeyDown={onHeaderKeyDown(col)}
                            disabled={!col.sortable}
                            className={`min-w-0 flex-1 flex-wrap justify-start whitespace-normal px-2 ${active ? 'text-primary' : ''}`}
                            title={col.sortable ? `Sort by ${col.label}; Shift+click adds it as a secondary sort` : col.label}
                            aria-label={
                              col.sortable
                                ? `${col.label}. ${
                                    active
                                      ? `Sorted ${dir === 'asc' ? 'ascending' : 'descending'}${
                                          sortLevels.length > 1 ? `, level ${level + 1}` : ''
                                        }.`
                                      : 'Not sorted.'
                                  } Click to sort; Shift+click to add as a secondary sort.`
                                : col.label
                            }
                          >
                            <span className="min-w-0 wrap-break-word text-left">{col.label}</span>
                            {col.sortable && (
                              <span aria-hidden className="text-xs shrink-0">
                                {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
                                {active && sortLevels.length > 1 && <sup>{level + 1}</sup>}
                              </span>
                            )}
                          </Button>
                          <HeaderHelp col={col} canFilter={col.filterable && !activeFilterKeys.includes(filterKeyOf(col))} />
                        </div>
                        {/* Quick close: hides the column, the same as unticking it in
                            Choose columns. Shown on hover or keyboard focus; its own
                            click never sorts, and it never starts a drag. */}
                        <span className="absolute left-0 top-0 z-20 rounded-full bg-background shadow-sm opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto">
                          <Button
                            variant="icon"
                            size="sm"
                            className="h-4 w-4 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleColumn(col.key, false);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            draggable={false}
                            onDragStart={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            aria-label={`Hide the ${col.label} column`}
                            title={`Hide the ${col.label} column. Bring it back from Choose columns.`}
                            data-testid={`hide-column-${col.key}`}
                          >
                            <span aria-hidden>×</span>
                          </Button>
                        </span>
                        {/* Quick filter: adds this column's filter above the table.
                            Only for a column with a filter that is not already
                            there; same hover/focus reveal as the × above it. */}
                        {col.filterable && !activeFilterKeys.includes(filterKeyOf(col)) && (
                          <span className="absolute left-0 bottom-0.5 z-20 rounded-full bg-background shadow-sm opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto">
                            <Button
                              variant="icon"
                              size="sm"
                              className="h-4 w-4"
                              onClick={(e) => {
                                e.stopPropagation();
                                addFilterFromHeader(filterKeyOf(col));
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              draggable={false}
                              onDragStart={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              aria-label={`Filter on ${col.label}`}
                              title={`Filter on ${col.label}: adds a ${col.label} filter above the table, ready for a value.`}
                              data-testid={`filter-column-${col.key}`}
                            >
                              <FilterIcon size={10} aria-hidden />
                            </Button>
                          </span>
                        )}
                        {/* resize handle */}
                        <div
                          className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize touch-none select-none hover:bg-blue-200"
                          onPointerDown={startResize(col)}
                          onDoubleClick={() => resetWidth(col.key)}
                          onClick={(e) => e.stopPropagation()}
                          draggable={false}
                          onDragStart={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`Resize ${col.label} column`}
                          data-testid={`resize-${col.key}`}
                        />
                      </th>
                    );
                  })}
                  {canWrite && (
                    <th scope="col" data-colkey="actions" className="py-2 pr-4 font-medium align-top">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr
                    key={d.id}
                    className={`border-b last:border-0 hover:bg-gray-100 cursor-pointer ${
                      selection.selected.has(d.id) ? 'bg-blue-50' : ''
                    }`}
                    onClick={(e) => openDevice(d.id, e)}
                  >
                    <td
                      data-select-col
                      className="p-0 align-top cursor-default"
                      onClick={onSelectCellClick(d.id)}
                      onMouseDown={noShiftTextSelect}
                    >
                      <div className="flex justify-center py-2">
                        <label id={`device-list-select-${d.id}-label`} htmlFor={`device-list-select-${d.id}`} className="sr-only">Select device {d.id}</label>
                        <Checkbox
                          id={`device-list-select-${d.id}`}
                          checked={selection.selected.has(d.id)}
                          // Click carries Shift for contiguous row selection.
                          onClick={onSelectBoxClick(d.id)}
                          aria-labelledby={`device-list-select-${d.id}-label`}
                          title={`${selection.selected.has(d.id) ? 'Unselect' : 'Select'} device ${d.id}. Shift+click selects every row from the last box you clicked to this one.`}
                          className="cursor-pointer"
                        />
                      </div>
                    </td>
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        data-colkey={col.key}
                        className="py-2 pr-4 overflow-hidden align-top"
                      >
                        {col.key === 'source' ? sourceCell(d.name) : col.render(d)}
                      </td>
                    ))}
                    {canWrite && (
                      <td data-colkey="actions" className="py-2 pr-4 align-top">
                        <Button
                          variant="primary"
                          data-edit-device-id={d.id}
                          onClick={() => handleEdit(d)}
                          disabled={editLoadingId === d.id}
                          title="Open this device's record (tags, calibration offset, address, assembly, time zone) in an editor"
                        >
                          {editLoadingId === d.id ? 'Loading…' : 'Edit'}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paging controls */}
          <div className="mt-4 flex items-center justify-between gap-4 flex-wrap text-sm">
            <div className="flex flex-wrap items-center gap-2 text-gray-600">
              <span>
                {first}–{last} of {total}
              </span>
              <div className="ml-3 flex items-center gap-1">
                <label htmlFor="device-list-page-size">Per page:</label>
                <Select value={String(pageSize)} onValueChange={(next) => { if (next) setParam('page_size', next); }}>
                  <SelectTrigger id="device-list-page-size" aria-label="Per page: devices" title="Choose how many devices to show on each page" className="min-w-18">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <PageBtn label="« First" title="Go to the first page of devices" disabled={page <= 1} onClick={() => goToPage(1)} />
              <PageBtn label="‹ Prev" title="Go to the previous page of devices" disabled={page <= 1} onClick={() => goToPage(page - 1)} />
              <span className="px-2 text-gray-600">
                Page {page} of {pageCount}
              </span>
              <PageBtn label="Next ›" title="Go to the next page of devices" disabled={page >= pageCount} onClick={() => goToPage(page + 1)} />
              <PageBtn label="Last »" title="Go to the last page of devices" disabled={page >= pageCount} onClick={() => goToPage(pageCount)} />
            </div>
          </div>
        </div>
      )}

      {selectedDevice && (
        <ConfirmDialog
          open
          title="Edit Device"
          returnFocusTo={`[data-edit-device-id="${selectedDevice.id}"]`}
          restoreOnUnmount
          maxWidth="2xl"
          confirmLabel="Save"
          confirmTitle={editSaveBlocked ?? 'Save the changes to this device and close the editor'}
          confirmDisabled={!!editSaveBlocked}
          disabledReason={editSaveBlocked ?? undefined}
          submitting={editSaving}
          error={editError}
          onConfirm={() => void handleSave()}
          onCancel={closeEdit}
        >
            <DeviceAppUserData enrichment={selectedEnrichment} loading={enrichmentLoading} error={enrichmentError} />
            {/* FS-501: tags attach immediately rather than on Save, because
                each one is its own server call (and can create a tag). The
                note says so, so the operator is never left wondering whether
                closing without saving undoes a tag. */}
            <div className="mb-4">
              <Label className="block text-sm font-medium mb-1">
                Tags:
                <span className="text-xs text-muted-foreground ml-1">(saved immediately)</span>
              </Label>
              <DeviceTagsPanel deviceId={selectedDevice.id} onTagsChanged={refetch} />
            </div>
            <DeviceRecordFields
              draft={selectedDevice}
              onChange={changeEdit}
              canWrite={canWrite}
              addressLocked={!!selectedEnrichment}
              entityPathOptions={entityPathOptions}
              entityPathOptionsError={entityPathOptionsError}
              scopes={scopes ?? []}
            />
        </ConfirmDialog>
      )}

      {/* FS-889: the report dialogs. Each runs its own request and shows a
          refusal under its heading; the page-level banner above is for the
          failures that happen after a useConfirm dialog has closed. */}
      {dialog?.kind === 'name' && (
        <ReportNameDialog
          open
          title={dialog.purpose === 'create' ? 'Save report' : dialog.purpose === 'copy' ? 'Save a copy' : 'Rename report'}
          confirmLabel={dialog.purpose === 'rename' ? 'Rename' : 'Save'}
          initialName={
            dialog.purpose === 'rename'
              ? (open?.report?.name ?? '')
              : dialog.purpose === 'copy'
                ? `Copy of ${open?.report?.name ?? 'report'}`.slice(0, 80)
                : ''
          }
          currentName={dialog.purpose === 'rename' ? open?.report?.name : undefined}
          myReportNames={myReportNames}
          savesPrivate={dialog.purpose !== 'rename'}
          returnFocusTo={REPORT_TRIGGER_SELECTOR}
          onSubmit={(name) => submitName(dialog.purpose, dialog.view, name)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'share' && (
        <ShareReportDialog
          open
          report={dialog.report}
          candidate={dialog.candidate}
          candidateIsWorkingView={dialog.candidateIsWorkingView}
          savesState={dialog.savesState}
          facetLabel={facetLabel}
          returnFocusTo={REPORT_TRIGGER_SELECTOR}
          onSubmit={(args) => submitShare(dialog.report, dialog.candidate, dialog.savesState, args)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'saveShared' && (
        <SaveSharedReportDialog
          open
          report={dialog.report}
          view={dialog.view}
          facetLabel={facetLabel}
          returnFocusTo={REPORT_TRIGGER_SELECTOR}
          onSubmit={() => saveState(dialog.report, dialog.view, true)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'stale' && (
        <StaleReportDialog
          open
          reportName={dialog.report.name}
          returnFocusTo={REPORT_TRIGGER_SELECTOR}
          onReload={reloadSaved}
          onKeepEditing={() => setDialog(null)}
        />
      )}
    </div>
  );
};

const PageBtn: React.FC<{ label: string; title: string; disabled: boolean; onClick: () => void }> = ({
  label,
  title,
  disabled,
  onClick,
}) => (
  <Button variant="secondary" size="sm" onClick={onClick} disabled={disabled} title={title}>
    {label}
  </Button>
);

const HeaderHelp: React.FC<{ col: ColumnDef; canFilter: boolean }> = ({ col, canFilter }) => {
  const [open, setOpen] = useState(false);
  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger
          render={
            <Button
              variant="icon"
              size="sm"
              aria-label={`Help for ${col.label}`}
              title={`Explain ${col.label} and its column actions`}
              onClick={(event) => {
                event.stopPropagation();
                setOpen((current) => !current);
              }}
              className="ml-1 size-6 shrink-0"
            />
          }
        >
          ?
        </TooltipTrigger>
        <TooltipContent>
          {col.tooltip ? `${col.tooltip}. ` : ''}
          {col.sortable ? 'Shift+click adds a secondary sort. ' : ''}
          Drag to reorder; drag the right edge to resize; the × hides the column
          {canFilter ? ' and the funnel adds a filter' : ''}.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default DeviceList;
